<script setup lang="ts">
import { BaseDialog } from '@cplug/design-system/components'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateFormats, useFormatTemporalDateTime } from '@cplug/frontend-core/composables'
import { createInstantSafe } from '~/shared/composables/useTemporal'
import { useAppBuildInfo } from '~/shared/composables/useAppBuildInfo'
import {
  isProductionBuildEnvironment,
  resolveProductionDisplayVersion
} from '~/shared/models/AppBuildInfo'

const open = defineModel<boolean>({ required: true })

const { t } = useI18n()
const { buildInfo, error, loadBuildInfo, loading } = useAppBuildInfo()

const SAO_PAULO_TIME_ZONE = 'America/Sao_Paulo'

const builtAtDisplay = computed(() => {
  const builtAt = buildInfo.value?.build.builtAt
  if (!builtAt) {
    return undefined
  }
  return formatBuiltAt(builtAt)
})

const isProductionView = computed(() => {
  return Boolean(
    buildInfo.value && isProductionBuildEnvironment(buildInfo.value.build.environment)
  )
})

const productionDisplayVersion = computed(() => {
  if (!buildInfo.value) {
    return undefined
  }
  return resolveProductionDisplayVersion(buildInfo.value.app)
})

const showDeclaredVersion = computed(() => {
  return Boolean(
    buildInfo.value &&
    !isProductionView.value &&
    !buildInfo.value.app.releaseTag
  )
})

watch(open, async (isOpen) => {
  if (isOpen) {
    await loadBuildInfo()
  }
})

function formatPipelineLabel (pipelineId?: string, jobName?: string): string {
  if (pipelineId && jobName) {
    return `${pipelineId} · ${jobName}`
  }
  if (pipelineId) {
    return pipelineId
  }
  if (jobName) {
    return jobName
  }
  return t('AppVersionInfoNotAvailable')
}

function formatBuiltAt (iso: string): { formatted: string; iso: string } {
  const instant = createInstantSafe(iso)
  if (!instant) {
    return { formatted: iso, iso }
  }
  const zoned = instant.toZonedDateTimeISO(SAO_PAULO_TIME_ZONE)
  const formatted = useFormatTemporalDateTime(zoned.toPlainDateTime(), DateFormats.ddMMYYYHHMM)
  return { formatted, iso }
}

function formatDependencyVersion (version: string): string {
  return version.startsWith('v') ? version : `v${version}`
}

function formatReleaseTag (tag: string): string {
  return tag.startsWith('v') ? tag : `v${tag}`
}
</script>

<template>
  <BaseDialog
    v-model="open"
    width="480px"
    :title="t('AppVersionInfoTitle')"
    :dismissable-mask="true"
  >
    <template #header>
      <div
        class="px-4 py-3 text-base font-semibold text-foreground"
        data-testid="app-version-info-title"
      >
        {{ t('AppVersionInfoTitle') }}
      </div>
    </template>

    <div
      class="flex flex-col gap-4 px-4 pb-4 text-sm text-foreground"
      data-testid="app-version-info-dialog"
    >
      <div
        v-if="loading"
        class="text-muted-foreground"
        data-testid="app-version-info-loading"
      >
        {{ t('AppVersionInfoLoading') }}
      </div>

      <div
        v-else-if="error || !buildInfo"
        class="text-muted-foreground"
        data-testid="app-version-info-error"
      >
        {{ t('AppVersionInfoLoadError') }}
      </div>

      <template v-else>
        <p
          v-if="isProductionView"
          class="text-base text-foreground"
          data-testid="app-version-info-production-version"
        >
          {{ t('AppVersionInfoProductionVersion', { version: productionDisplayVersion }) }}
        </p>

        <template v-else>
          <p
            class="body-small-regular text-muted-foreground"
            data-testid="app-version-info-build-label"
          >
            {{ buildInfo.app.buildLabel }}
          </p>

          <section>
            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {{ t('AppVersionInfoPackagesSection') }}
            </h3>
            <dl class="space-y-1.5">
              <div
                v-for="(version, packageName) in buildInfo.dependencies"
                :key="packageName"
                class="flex flex-col gap-0.5 sm:flex-row sm:gap-2"
              >
                <dt class="shrink-0 text-muted-foreground">
                  {{ packageName }}
                </dt>
                <dd class="break-all font-mono text-xs sm:text-sm">
                  {{ formatDependencyVersion(version) }}
                </dd>
              </div>
            </dl>
          </section>

          <section>
            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {{ t('AppVersionInfoBuildSection') }}
            </h3>
            <dl class="space-y-1.5">
              <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                <template v-if="buildInfo.app.releaseTag">
                  <dt class="text-muted-foreground">
                    {{ t('AppVersionInfoRelease') }}
                  </dt>
                  <dd class="font-mono text-xs">
                    {{ formatReleaseTag(buildInfo.app.releaseTag) }}
                  </dd>
                </template>

                <dt class="text-muted-foreground">
                  {{ t('AppVersionInfoEnvironment') }}
                </dt>
                <dd>{{ buildInfo.build.environment }}</dd>

                <dt class="text-muted-foreground">
                  {{ t('AppVersionInfoBranch') }}
                </dt>
                <dd class="font-mono text-xs">
                  {{ buildInfo.build.branch }}
                </dd>

                <dt class="text-muted-foreground">
                  {{ t('AppVersionInfoCommit') }}
                </dt>
                <dd class="font-mono text-xs">
                  <a
                    v-if="buildInfo.build.commitUrl"
                    :href="buildInfo.build.commitUrl"
                    class="text-primary underline-offset-4 hover:underline"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {{ buildInfo.build.commitSha }}
                  </a>
                  <span v-else>
                    {{ buildInfo.build.commitSha }}
                  </span>
                </dd>

                <dt class="text-muted-foreground">
                  {{ t('AppVersionInfoPipeline') }}
                </dt>
                <dd>
                  <a
                    v-if="buildInfo.build.pipelineUrl"
                    :href="buildInfo.build.pipelineUrl"
                    class="text-primary underline-offset-4 hover:underline"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {{ formatPipelineLabel(buildInfo.build.pipelineId, buildInfo.build.jobName) }}
                  </a>
                  <span v-else>
                    {{ formatPipelineLabel(buildInfo.build.pipelineId, buildInfo.build.jobName) }}
                  </span>
                </dd>

                <dt class="text-muted-foreground">
                  {{ t('AppVersionInfoBuiltAt') }}
                </dt>
                <dd
                  v-if="builtAtDisplay"
                  class="flex flex-col gap-0.5"
                >
                  <span>{{ builtAtDisplay.formatted }}</span>
                  <span class="font-mono text-xs text-muted-foreground">
                    {{ builtAtDisplay.iso }}
                  </span>
                </dd>
              </div>
            </dl>
          </section>

          <p
            v-if="showDeclaredVersion"
            class="body-small-regular text-muted-foreground"
            data-testid="app-version-info-declared-version"
          >
            {{ t('AppVersionInfoDeclaredVersion', { version: buildInfo.app.packageVersion }) }}
          </p>
        </template>
      </template>
    </div>
  </BaseDialog>
</template>

<i18n lang="yaml">
pt-br:
  AppVersionInfoBranch: 'Branch'
  AppVersionInfoBuildSection: 'Deploy'
  AppVersionInfoBuiltAt: 'Gerado em'
  AppVersionInfoCommit: 'Commit'
  AppVersionInfoDeclaredVersion: 'Versão declarada (package.json): {version}'
  AppVersionInfoEnvironment: 'Ambiente'
  AppVersionInfoLoadError: 'Não foi possível carregar as informações de versão.'
  AppVersionInfoLoading: 'Carregando informações…'
  AppVersionInfoNotAvailable: '—'
  AppVersionInfoPackagesSection: 'Pacotes'
  AppVersionInfoPipeline: 'Pipeline'
  AppVersionInfoProductionVersion: 'Versão: {version}'
  AppVersionInfoRelease: 'Release'
  AppVersionInfoTitle: 'Informações'
en-US:
  AppVersionInfoBranch: 'Branch'
  AppVersionInfoBuildSection: 'Deploy'
  AppVersionInfoBuiltAt: 'Built at'
  AppVersionInfoCommit: 'Commit'
  AppVersionInfoDeclaredVersion: 'Declared version (package.json): {version}'
  AppVersionInfoEnvironment: 'Environment'
  AppVersionInfoLoadError: 'Could not load version information.'
  AppVersionInfoLoading: 'Loading…'
  AppVersionInfoNotAvailable: '—'
  AppVersionInfoPackagesSection: 'Packages'
  AppVersionInfoPipeline: 'Pipeline'
  AppVersionInfoProductionVersion: 'Version: {version}'
  AppVersionInfoRelease: 'Release'
  AppVersionInfoTitle: 'Information'
</i18n>
